import React from 'react';
import styled from 'styled-components';
import { Button, Typography, Result } from 'antd';
import { LockOutlined, HomeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const { Title, Text, Paragraph } = Typography;

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xl};
`;

const ContentCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xxl};
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const IconWrapper = styled.div`
  font-size: 64px;
  color: ${({ theme }) => theme.colors.warning};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const RoleInfo = styled.div`
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin: ${({ theme }) => theme.spacing.lg} 0;
`;

const RoleBadge = styled.span`
  display: inline-block;
  background: color-mix(in srgb, ${({ theme }) => theme.colors.accent} 13%, transparent);
  color: ${({ theme }) => theme.colors.accent};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: ${({ theme }) => theme.spacing.xs};
`;

const UnauthorizedPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <PageContainer>
      <ContentCard>
        <IconWrapper>
          <LockOutlined />
        </IconWrapper>

        <Title level={2} style={{ color: 'var(--odin-text-primary)', marginBottom: 8 }}>
          Access Denied
        </Title>

        <Text style={{ color: 'var(--odin-text-secondary)', fontSize: 16 }}>
          You don't have permission to access this page.
        </Text>

        {user && (
          <RoleInfo>
            <Text style={{ color: 'var(--odin-text-secondary)', display: 'block', marginBottom: 8 }}>
              Logged in as: <strong style={{ color: 'var(--odin-text-primary)' }}>{user.name || user.email}</strong>
            </Text>
            <Text style={{ color: 'var(--odin-text-secondary)', display: 'block', marginBottom: 8 }}>
              Your roles:
            </Text>
            {user.roles && user.roles.length > 0 ? (
              user.roles.map((role) => (
                <RoleBadge key={role}>{role}</RoleBadge>
              ))
            ) : (
              <Text style={{ color: 'var(--odin-text-tertiary)', fontStyle: 'italic' }}>
                No roles assigned
              </Text>
            )}
          </RoleInfo>
        )}

        <Paragraph style={{ color: 'var(--odin-text-secondary)', marginTop: 16 }}>
          If you believe you should have access to this page, please contact your administrator
          to request the appropriate role assignment.
        </Paragraph>

        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 24 }}>
          <Button
            type="primary"
            size="large"
            icon={<HomeOutlined />}
            onClick={handleGoHome}
          >
            Go to Dashboard
          </Button>
          <Button
            size="large"
            onClick={handleLogout}
          >
            Sign Out
          </Button>
        </div>
      </ContentCard>
    </PageContainer>
  );
};

export default UnauthorizedPage;
